import { resolveUrl, verifyResponse } from '@/lib/api-utils';
import AuthService from '@/services/AuthService';
import SparkMD5 from 'spark-md5';

export interface UploadedAttachment {
  checksum: string;
  size: number;
  contentType: string;
  filename: string | null;
}

/**
 * Hash a file with SparkMD5 and upload its binary to the attachments endpoint.
 * The server de-duplicates by checksum, so this is safe to call multiple times
 * for the same file.  Returns metadata the caller needs to create a
 * domain-specific event.
 *
 * Resolves once the server has accepted the upload, so an event created from
 * the result never references a file the server doesn't have yet (the
 * timeline would otherwise request a thumbnail that 404s). Offline, the
 * service worker answers the POST at once and queues it for sync, so this
 * still resolves immediately.
 */
export async function uploadAttachment(
  file: File,
): Promise<UploadedAttachment> {
  const checksum = SparkMD5.ArrayBuffer.hash(await file.arrayBuffer());

  await AuthService.getInstance().waitForReady();
  await fetch(resolveUrl(`/attachments/${checksum}`), {
    method: 'POST',
    credentials: 'include',
    headers: {
      'content-type': file.type,
      ...(file.name ? { 'x-filename': file.name } : {}),
    },
    body: file,
  }).then(verifyResponse);

  return {
    checksum,
    size: file.size,
    contentType: file.type,
    filename: file.name || null,
  };
}
